import React from 'react';

import PropTypes from 'prop-types';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { connect, useDispatch, useSelector } from 'react-redux';

import socketContext from 'components/contentWrapper/socketContext';
import mqttContext from 'components/contentWrapper/mqttContext';
import UserSessionDataHandler from 'auth/UserSessionsDataHandler';
import { addAlert } from 'store/actions/alert/alerts';
import { addLayerToMap, removeComponentLayers } from 'utils/map/map';
import { authPatchRequestWithParam } from 'helpers/apiRequests';
import { createLayer } from 'utils/map/layers';
import {
  filterUavosSources,
  updateUavosOnMap,
} from 'components/uavos/utils/updateUavosLayer';
import { formStyles } from 'consts/styles/Form';
import {
  getAllUavosInsideExtent,
  parseDrones,
} from 'components/uavos/utils/common';

import { hasObjectKeys } from 'utils/main';
import { UAVOS_SOURCES } from 'consts/mapConfig';

import { Grid } from '@material-ui/core';
import { getUserUidFromStorage } from './utils/main';

const UavosSourcesConfig = props => {
  const { map, mappingUavos } = props;
  const [activeSources, setActiveSource] = React.useState([]);
  const dispatch = useDispatch();
  const uavosLayer = React.useRef();
  const socket = React.useContext(socketContext);
  const mqtt = React.useContext(mqttContext);
  const aircrafts = React.useRef([]);
  const [isMapAvailable, setIsMapAvailable] = React.useState([]);
  const mapExtent = React.useRef([]);

  const init = () => {
    if (hasObjectKeys(map)) setIsMapAvailable(map?.getView().calculateExtent());
    mapExtent.current = isMapAvailable;
  };

  React.useEffect(init, [map]);

  const formClasses = formStyles();

  const useString = () => {
    const strings = useSelector(state => state.language);
    return strings;
  };
  const language = useString();

  const isSourceSelected = source =>
    !!activeSources.find(activeSource => activeSource === source);

  const handleSwitchChange = event => {
    const oldActiveSources = [...activeSources];
    const index = oldActiveSources.findIndex(
      item => item === event.target.name
    );
    if (index >= 0) oldActiveSources.splice(index, 1);
    else oldActiveSources.push(event.target.name);
    setActiveSource(oldActiveSources);
    updateUserConfiguration(oldActiveSources);
  };

  const toggleAllSources = () => {
    if (activeSources.length > 1) {
      setActiveSource([]);
      updateUserConfiguration([]);
    } else {
      setActiveSource(Object.values(UAVOS_SOURCES));
      updateUserConfiguration(Object.values(UAVOS_SOURCES));
    }
  };

  React.useEffect(() => {
    if (!hasObjectKeys(map)) return;
    uavosLayer.current = createLayer('uavosLayer');
    addLayerToMap(map, uavosLayer.current);

    return () => removeComponentLayers(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  React.useEffect(() => {
    setActiveSource(UserSessionDataHandler.getUavosFilters() || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMqttUpdate = () => {
    if (!socket) return;
    mqtt.on('message', (topic, encodedMessage, packet) => {
      const mqttAircrafts = JSON.parse(encodedMessage);
      if (!mqttAircrafts) return;
      aircrafts.current = mqttAircrafts;
    });

    return () => socket.off('uavosPositions');
  };

  React.useEffect(() => {
    if (!socket) return;
    socket.on('dronesPositions', socketDrones => {
      if (socketDrones === 'no data') return;
      const parsedDrones = parseDrones(socketDrones);
      handleSocketDataChange(
        getAllUavosInsideExtent(
          [...parsedDrones, ...aircrafts.current],
          mapExtent.current
        )
      );
    });
    return () => socket.off('uavosPositions');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // React.useEffect(handleSocketUpdate, [socket]);
  React.useEffect(handleMqttUpdate, [mqtt]);
  const updateUserConfiguration = sources => {
    const configData = UserSessionDataHandler.getUserConfig();
    const updatedConfigData = {
      ...configData,
      dashboard: { ...configData.dashboard, uavos: sources },
    };

    authPatchRequestWithParam('setUserConfiguration', getUserUidFromStorage(), {
      configuration: updatedConfigData,
    }).then(result => {
      if (result.status === 200) {
        UserSessionDataHandler.saveToUserSettingsObjectInStorage(
          UserSessionDataHandler.defaultDashboard,
          result.data.configuration.dashboard
        );
        UserSessionDataHandler.saveUavosFilters(sources);
      } else
        dispatch(
          addAlert({
            title: language.alerts[result.data.code].title_,
            desc: language.alerts[result.data.code].desc_,
            type: 'error',
          })
        );
    });
  };
  const handleSocketDataChange = allUavos => {
    if (!allUavos.length) return;
    updateUavosOnMap(
      uavosLayer.current,
      getFilteredUavos(allUavos),
      mappingUavos
    );
  };
  const getFilteredUavos = allUavos =>
    filterUavosSources(
      allUavos,
      UserSessionDataHandler.getUavosFilters() || []
    );

  const uavosSources = Object.keys(UAVOS_SOURCES).map((source, index) => (
    <Grid item xs={3} key={index}>
      <Switch
        className="switch"
        classes={{
          checked: `${formClasses.Form}__switch`,
          track: `${formClasses.Form}__switch__track`,
        }}
        checked={isSourceSelected(UAVOS_SOURCES[source])}
        onChange={handleSwitchChange}
        name={UAVOS_SOURCES[source]}
        inputProps={{ 'aria-label': source }}
      />
      <span className={`${formClasses.Form}__text-field`}>
        {language.dashboardConfig.uavos[`${source}_`]}
      </span>
    </Grid>
  ));

  const uavosAll = (
    <Grid item xs={3} key="alluavos">
      <Switch
        className="switch"
        classes={{
          checked: `${formClasses.Form}__switch`,
          track: `${formClasses.Form}__switch__track`,
        }}
        checked={Object.keys(UAVOS_SOURCES).length === activeSources.length}
        onChange={toggleAllSources}
        name="all"
        inputProps={{ 'aria-label': 'all' }}
      />
      <span className={`${formClasses.Form}__text-field`}>
        {language.dashboardConfig.uavos.all_}
      </span>
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography
          variant="h3"
          align="center"
          className={`${formClasses.Form}__text-field ${formClasses.Form}__text-field__title`}
        >
          {language.dashboardConfig.uavos.title_}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={3}>
          {uavosSources}
          {uavosAll}
        </Grid>
      </Grid>
    </Grid>
  );
};

UavosSourcesConfig.propTypes = {
  map: PropTypes.object.isRequired,
  mappingUavos: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  map: state.map,
  mappingUavos: state.mappingUavos,
});

export default connect(mapStateToProps)(UavosSourcesConfig);
